<template>
  <BaseDialog
    :title="$t('coaching.feedbackGoals.editNumbersDialog.title')"
    :is-open="isOpen"
    type="responsive"
    :align-dismiss-button-right="true"
    @close="closeDialog"
  >
    <ul class="flex flex-col">
      <p class="text-body-medium text-on-surface-variant mb-2">
        {{ feedbackGoal.feedbackTopic.title }}
      </p>
      <p v-if="errors.get('perspectiveCounts')" class="text-error text-body-small mb-2">
        {{ errors.get('perspectiveCounts') }}
      </p>
      <div
        class="[&>div]:rounded-extra-small [&>div]:last:rounded-b-large [&>div]:first:rounded-t-large flex w-full flex-col gap-0.5"
      >
        <EvaluationPerspectiveGoalSetter
          v-for="p in goalPerspectives"
          :key="p.key"
          :is-active="true"
          :perspective="p"
          :label="labelFor(p)"
          :number-of-feedbacks="getPerspectiveCount(p)"
          @plus="adjustPerspectiveCount(p, 'add')"
          @minus="adjustPerspectiveCount(p, 'remove')"
          @update:number-of-feedbacks="setPerspectiveCount(p, $event)"
        />
      </div>
    </ul>
    <template #confirm>
      <BaseTextButton color="filled" size="M" :disabled="isSaving" @click="saveEdits">{{
        $t('common.save')
      }}</BaseTextButton>
    </template>
    <template #dismiss>
      <BaseTextButton color="text" size="M" :disabled="isSaving" @click="closeDialog">{{
        $t('common.cancel')
      }}</BaseTextButton>
    </template>
  </BaseDialog>
</template>

<script setup lang="ts">
  import { computed, toRef } from 'vue'
  import { useI18n } from 'vue-i18n'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import BaseDialog from '@/core/components/base/dialog/BaseDialog.vue'
  import EvaluationPerspectiveGoalSetter from '@/modules/coachees/components/EvaluationPerspectiveGoalSetter.vue'
  import type { Perspective } from '@/core/models/perspective'
  import { resolvePerspectiveLabel } from '@/core/models/perspective-label'
  import type { CoachFeedbackGoal } from '../composables/use-coach-feedback-goal'
  import { useEditCoachFeedbackGoal } from '../composables/use-edit-coach-feedback-goal'

  interface Props {
    isOpen: boolean
    feedbackGoal: CoachFeedbackGoal
  }

  const props = defineProps<Props>()

  const emit = defineEmits<{
    (e: 'close-dialog'): void
    (e: 'goal-updated'): void
  }>()

  const { t } = useI18n()

  const closeDialog = () => emit('close-dialog')

  const {
    saveGoal,
    errors,
    goalPerspectives,
    getPerspectiveCount,
    adjustPerspectiveCount,
    setPerspectiveCount,
    isSaving,
  } = useEditCoachFeedbackGoal(
    computed(() => props.feedbackGoal),
    toRef(props, 'isOpen'),
    () => {
      emit('goal-updated')
      emit('close-dialog')
    },
  )

  const labelFor = (perspective: Perspective) =>
    resolvePerspectiveLabel(perspective.key, props.feedbackGoal.feedbackTopic.perspectives, {
      kind: 'title',
      selfLabel: t('perspectives.self.default'),
    })

  const saveEdits = async () => {
    await saveGoal()
  }
</script>
